import { useMemo } from "react";

import { filter, map, reject } from "lodash/fp";

import { ProgressItemEnum } from "@app/analytics/GettingStarted/types";
import { getProgressItemsFromProgress } from "@app/analytics/GettingStarted/utils";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";

import useGetCalendarState from "./hooks/useGetCalendarState";
import useGetIntegrationState from "./hooks/useGetIntegrationState";
import useGetMeetingInviteState from "./hooks/useGetMeetingInviteState";
import useGetMeetingReportState from "./hooks/useGetMeetingReportState";

const useGettingStarted = () => {
  const { userMetadata } = useUserMetadata();
  const progress = userMetadata?.onboardingChecklistProgress ?? 0;

  const { canConnectCalendars } = useFeatureAccess();

  const progressItems = useMemo(() => {
    const items = getProgressItemsFromProgress(progress);
    // remove the calendar item if the user can't connect calendars
    if (!canConnectCalendars) {
      return reject({ type: ProgressItemEnum.CALENDAR }, items);
    }
    return items;
  }, [progress, canConnectCalendars]);

  const calendarState = useGetCalendarState(progress);
  const inviteState = useGetMeetingInviteState(progress);
  const meetingReportState = useGetMeetingReportState(progress);
  const integrationState = useGetIntegrationState(progress);

  const itemStates = [
    calendarState,
    inviteState,
    meetingReportState,
    integrationState,
  ];

  const itemsPendingCompletion: ProgressItemEnum[] = useMemo(
    () => {
      return map("type", filter({ state: "unmarked_complete" }, itemStates));
    },
    map("state", itemStates)
  );

  return {
    progressItems,
    itemsPendingCompletion,
  };
};

export default useGettingStarted;
